/* eslint-disable react/jsx-no-target-blank */
import React, {useEffect, useState} from 'react'
import {
  isMegaPay,
  hasMegaPayBusinessCapabilities,
  isPOS,
  onTSP,
  isAPP,
  isTSP,
  PosBattery,
  PosSetting,
  AppClearCache,
  isOptional,
} from '../../../../helpers'
import {SidebarMenuItemWithSub} from './SidebarMenuItemWithSub'
import {SidebarMenuItem} from './SidebarMenuItem'
import {useAuth} from '../../../../../app/modules/auth'
import {canViewOneRowQueueInvoiceReport} from '../../../../../app/modules/auth/core/oneRowQueueInvoiceReportAccess'
import {
  canViewIsunReport,
  isIsunReportAdministrator,
} from '../../../../../app/modules/auth/core/isunReportAccess'
import {canViewShahrReport} from '../../../../../app/modules/auth/core/shahrReportAccess'
import {canUseTicketing} from '../../../../../app/modules/auth/core/ticketingAccess'
import {canViewSmsLog} from '../../../../../app/modules/auth/core/smsLogAccess'
import {canMonitorAccountingSync} from '../../../../../app/modules/auth/core/accountingSyncMonitorAccess'
import {isOnPremisesInstallation} from '../../../../../app/modules/auth/core/onPremisesAccess'
import {
  isStaff,
  staffCan,
  staffPicksBusiness,
} from '../../../../../app/modules/auth/core/staffAccess'
import {
  MY_BUSINESSES_PATH,
  hasManyBusinesses,
} from '../../../../../app/modules/auth/core/postLoginPath'
import {getStuffIdSearchUrl} from '../../../../../app/modules/apps/stuffid-management/stuffidSite'
import {
  getTaxWorkspaceProfile,
  readWorkspaceError,
} from '../../../../../app/modules/apps/tax-workspace/core/api'
import {businessSettingsPath} from '../../../../../app/modules/online-payment/core/businessSettingsPath'
import {getWorkspaceAvailable} from '../../../../../app/modules/apps/partner-workspace/core/_requests'
import {findFeature, getServiceCatalog} from '../../../../../app/modules/business-services/core/catalogApi'
import {isSupplyChainStaff} from '../../../../../app/modules/supply-chain/core/access'
import {
  SUPPLY_CHAIN_BUSINESS_PATHS,
  SUPPLY_CHAIN_FEATURE,
  SUPPLY_CHAIN_OVERVIEW,
  SUPPLY_CHAIN_STAFF_PAGES,
  SUPPLY_CHAIN_STAFF_ROOT,
} from '../../../../../app/modules/supply-chain/core/sections'

const SidebarMenuMain = () => {
  const {currentUser} = useAuth()
  const isSupportSession = currentUser?.support?.session_type === 'support'
  // Staff granted «کارپوشه مالیاتی» reach every business's tax workspace and pick it on the page.
  const staffTaxWorkspace = staffCan(currentUser, 'taxWorkspace')
  // Staff who pick any business: the business pages open without one and offer the picker.
  const picksBusiness = staffPicksBusiness(currentUser)
  const noBusiness = (currentUser?.cpi || 0) == 0
  const isOnPremises = isOnPremisesInstallation(currentUser)
  const ticketingEnabled = canUseTicketing(currentUser)
  const oneRowQueueInvoiceReportEnabled = canViewOneRowQueueInvoiceReport(currentUser)
  const smsLogEnabled = canViewSmsLog(currentUser)
  const staffAccountingSync = canMonitorAccountingSync(currentUser) && !isOnPremises
  const staffMonitoring = staffCan(currentUser, 'monitoring')
  const staffServices = staffCan(currentUser, 'services')
  const staffPayments = staffCan(currentUser, 'payments')
  // «تنظیمات سیستمی» shows when the CRM role grants at least one of its pages.
  const hasSystemSettings =
    staffMonitoring ||
    staffServices ||
    staffPayments ||
    staffAccountingSync ||
    smsLogEnabled ||
    (isMegaPay && (staffCan(currentUser, 'users') || staffCan(currentUser, 'catalog'))) ||
    staffCan(currentUser, 'partners')
  const [canViewTaxWorkspace, setCanViewTaxWorkspace] = useState(false)
  const [taxWorkspaceDisabled, setTaxWorkspaceDisabled] = useState(false)
  // «پیشخوان همکاری» is offered only when the selected business is entered in «همکاران API».
  const [isPartnerBusiness, setIsPartnerBusiness] = useState(false)
  // «زنجیره تأمین» is in the business's menu once ASA turned the service on for it; ASA's staff have the module's own
  // group.
  const [supplyChainOn, setSupplyChainOn] = useState(false)
  const supplyChainStaff = isSupplyChainStaff(currentUser) && !isOnPremises

  useEffect(() => {
    let active = true
    setSupplyChainOn(false)
    if (isOnPremises || onTSP || !currentUser?.cpi) {
      return () => {
        active = false
      }
    }

    getServiceCatalog(Number(currentUser.cpi))
      .then((catalog) => {
        if (active) setSupplyChainOn(Boolean(findFeature(catalog, SUPPLY_CHAIN_FEATURE)?.active))
      })
      .catch(() => {
        // Not offered when the catalog cannot be read; the page itself says why if opened by address.
        if (active) setSupplyChainOn(false)
      })

    return () => {
      active = false
    }
  }, [currentUser?.cpi, currentUser?.id, isOnPremises])

  useEffect(() => {
    let active = true
    setIsPartnerBusiness(false)
    if (isOnPremises || isSupportSession || !currentUser?.cpi) {
      return () => {
        active = false
      }
    }

    getWorkspaceAvailable()
      .then((answer) => {
        if (active) setIsPartnerBusiness(Boolean(answer.available))
      })
      .catch(() => {
        // Not offered when the answer cannot be read; the page itself says why if opened by address.
        if (active) setIsPartnerBusiness(false)
      })

    return () => {
      active = false
    }
  }, [currentUser?.cpi, currentUser?.id, isOnPremises, isSupportSession])

  useEffect(() => {
    let active = true
    if (isOnPremises || !currentUser?.cpi) {
      setCanViewTaxWorkspace(false)
      setTaxWorkspaceDisabled(false)
      return () => {
        active = false
      }
    }

    setCanViewTaxWorkspace(false)
    setTaxWorkspaceDisabled(false)
    getTaxWorkspaceProfile()
      .then((profile) => {
        if (active) {
          setCanViewTaxWorkspace(Boolean(profile.internalPermissions?.view))
          setTaxWorkspaceDisabled(false)
        }
      })
      .catch((requestError) => {
        if (active) {
          setCanViewTaxWorkspace(false)
          setTaxWorkspaceDisabled(
            readWorkspaceError(requestError, 'وضعیت کارپوشه مالیاتی دریافت نشد.').code ===
              'tax_workspace_disabled'
          )
        }
      })

    return () => {
      active = false
    }
  }, [currentUser?.cpi, currentUser?.id, isOnPremises, isSupportSession])
  return (
    <>
      {/* A user of more than one business sees all of them, with what waits in each, before the business's own pages. */}
      {hasManyBusinesses(currentUser) && (
        <SidebarMenuItem to={MY_BUSINESSES_PATH} title='کسب‌وکارهای من' fontIcon='las la-th-large' />
      )}
      <SidebarMenuItem
        to='/dashboard'
        icon='element-11'
        title='داشبورد'
        fontIcon='bi bi-app-indicator'
        businessFilter
      />
      {/* The invoice lists come first, right after the dashboard. */}
      <SidebarMenuItem
        // Staff granted «صورتحساب‌ها» list every business's newest invoices without picking one.
        isDisabled={noBusiness && !picksBusiness && !staffCan(currentUser, 'invoices')}
        to='/apps/invoice-management/invoices'
        title='صورتحساب‌های فروش'
        fontIcon='la la-file-invoice'
        businessFilter
      />
      {/* One entry: the operations report and the connection settings are tabs of the page. */}
      {!isOnPremises && (
        <SidebarMenuItem
          to='/apps/accounting-sync/invoices'
          title='صورتحساب‌های حسابداری'
          fontIcon='la la-plug'
          // Staff pick the business on the page itself.
          isDisabled={noBusiness && !picksBusiness}
          activePaths={[
            '/apps/accounting-sync/return-aggregates',
            '/apps/accounting-sync/logs',
            '/apps/accounting-sync/settings',
          ]}
          businessFilter
        />
      )}
      {!isOnPremises && !isSupportSession && (
        <SidebarMenuItem
          // Card-reader transactions and bank-account deposits belong to one business; staff
          // picks it on the page itself. Both are on this one page (user ask 2026-10-18).
          isDisabled={noBusiness && !picksBusiness}
          to='/apps/taxpayer-management/online-pos'
          title='صورتحساب‌های کارت‌خوان و بانک'
          fontIcon='la la-cash-register'
          businessFilter
        />
      )}
      <SidebarMenuItem
        isDisabled={noBusiness && !picksBusiness}
        to='/apps/invoice-management/archives'
        title='صورتحساب‌های آرشیو'
        fontIcon='las la-archive'
        businessFilter
      />
      {!isSupportSession && (
        <SidebarMenuItem
          isDisabled={noBusiness && !picksBusiness}
          to='/apps/ebtal-management/ebtals'
          title='ابطال گروهی صورتحساب‌ها'
          fontIcon='la la-calendar-times'
          businessFilter
        />
      )}
      {!isOnPremises && (canViewTaxWorkspace || staffTaxWorkspace) && (
        <SidebarMenuItemWithSub
          to=''
          title='کارپوشه مالیاتی'
          fontIcon='la la-folder-open'
          isDisabled={noBusiness && !staffTaxWorkspace}
          activePaths={['/tax-workspace', '/apps/invoice-management/purchases']}
          businessFilter
        >
          <SidebarMenuItem
            to='/apps/invoice-management/purchases'
            title='صورتحساب‌های خرید کارپوشه'
            fontIcon='la la-cart-arrow-down'
          />
          <SidebarMenuItem
            to='/tax-workspace/sales-invoices'
            title='صورتحساب‌های فروش کارپوشه'
            fontIcon='la la-file-invoice-dollar'
          />
          <SidebarMenuItem
            to='/tax-workspace/virtual-purchase-invoices'
            title='صورتحساب‌های مجازی خرید'
            fontIcon='la la-cloud-download-alt'
          />
          <SidebarMenuItem
            to='/tax-workspace/virtual-sales-invoices'
            title='صورتحساب‌های مجازی فروش'
            fontIcon='la la-cloud-upload-alt'
          />
          <SidebarMenuItem
            to='/tax-workspace/brokerage-contracts'
            title='قراردادهای شخص ثالث'
            fontIcon='la la-file-contract'
          />
          <SidebarMenuItem
            to='/tax-workspace/logs'
            title='گزارش عملیات کارپوشه'
            fontIcon='la la-clipboard-list'
          />
          {/* A business-settings page of the selected business; without one there is nothing to open. */}
          <SidebarMenuItem
            to={businessSettingsPath(currentUser?.cpi || 0, 'tax-workspace-alerts')}
            title='اعلان‌ها و تأیید خودکار'
            fontIcon='la la-inbox'
            isDisabled={(currentUser?.cpi || 0) === 0}
          />
        </SidebarMenuItemWithSub>
      )}
      <SidebarMenuItem
        isDisabled={noBusiness && !picksBusiness && !staffCan(currentUser, 'reports')}
        to={oneRowQueueInvoiceReportEnabled ? '/OneRowQueueInvoice' : '/reports'}
        title='گزارش‌ها'
        fontIcon='la la-chart-pie'
        // «گزارش‌ها» fits a phone; the bank installation's queue report does not.
        isDesktopOnly={oneRowQueueInvoiceReportEnabled}
      />
      {isPartnerBusiness && (
        <SidebarMenuItem
          to='/apps/partner-workspace'
          title='پیشخوان همکاری'
          fontIcon='la la-handshake'
          activePaths={['/apps/partner-workspace']}
        />
      )}
      {/* A staff session inside a business has the business's page only by its own «زنجیره تأمین» level. */}
      {supplyChainOn && !isOnPremises && (!isStaff(currentUser) || supplyChainStaff) && (
        <SidebarMenuItem
          to={SUPPLY_CHAIN_OVERVIEW}
          title='زنجیره تأمین'
          fontIcon='la la-project-diagram'
          activePaths={SUPPLY_CHAIN_BUSINESS_PATHS}
        />
      )}
      {canViewIsunReport(currentUser) && (
        <SidebarMenuItem
          to='/reports/isun-daily'
          // Isun's users see their own report; staff see whose report it is.
          title={isIsunReportAdministrator(currentUser) ? 'آیسان' : 'گزارش روزانه فروش'}
          fontIcon='la la-chart-line'
          isDesktopOnly
        />
      )}
      {canViewShahrReport(currentUser) && (
        <SidebarMenuItem
          to='/reports/shahr-daily'
          title='بانک شهر'
          fontIcon='la la-university'
          isDesktopOnly
        />
      )}
      {hasMegaPayBusinessCapabilities() &&
        !isSupportSession &&
        taxWorkspaceDisabled &&
        currentUser && (
          <SidebarMenuItem
            isDisabled={(currentUser?.cpi || 0) == 0}
            to='/apps/invoice-management/purchases'
            title='صورتحساب‌های خرید'
            fontIcon='la la-cart-arrow-down'
            businessFilter
          />
        )}
      <SidebarMenuItem
        to='/apps/log-management/logs'
        title='لاگ‌ها'
        fontIcon='la la-history'
        isDesktopOnly
      />
      {isStaff(currentUser) && (
        <>
          {!isOnPremises && staffCan(currentUser, 'callCenter') && (
            <SidebarMenuItemWithSub
              to=''
              title='مرکز تماس'
              fontIcon='la la-headset'
              activePaths={['/callcenter']}
            >
              <SidebarMenuItem
                to='/callcenter/live'
                title='وضعیت لحظه‌ای'
                fontIcon='la la-broadcast-tower'
              />
              <SidebarMenuItem
                to='/callcenter/report'
                title='گزارش‌های تماس'
                fontIcon='la la-chart-bar'
              />
              <SidebarMenuItem to='/callcenter/calls' title='فهرست تماس‌ها' fontIcon='la la-list' />
            </SidebarMenuItemWithSub>
          )}
          {/* An on-premises install has no «تنظیمات سیستمی» (the user, 1405/07/25: «تنظیمات سیستمی هم روی فاش نداریم»). */}
          {!isOnPremises && hasSystemSettings && (
            <SidebarMenuItemWithSub
              to=''
              title='تنظیمات سیستمی'
              fontIcon='la la-cogs'
              activePaths={[
                '/monitoring/send-dashboard',
                '/monitoring/send-report',
                '/monitoring/send-watchdog',
                '/monitoring/sso-logins',
                '/monitoring/website-stats',
                '/apps/monitoring',
                '/apps/accounting-database-settings',
                '/apps/accounting-sync/monitor',
                '/apps/users',
                '/apps/partners',
                '/apps/stuffid-catalog-import',
                '/apps/StuffidFileTag-management',
                '/apps/sms-log-management',
                '/apps/wallet-settings',
                '/apps/payment-gateways-report',
                '/apps/services-monitor',
                '/apps/service-pricing',
              ]}
            >
              {staffMonitoring && (
                <SidebarMenuItem
                  to='/apps/monitoring'
                  title='مانیتورینگ سامانه'
                  fontIcon='la la-heartbeat'
                />
              )}
              {staffServices && (
                <SidebarMenuItem
                  to='/apps/services-monitor'
                  title='مانیتور سرویس‌ها و کیف پول'
                  fontIcon='la la-chart-pie'
                />
              )}
              {staffPayments && (
                <SidebarMenuItem
                  to='/apps/wallet-settings'
                  title='کیف پول و پرداخت آنلاین'
                  fontIcon='la la-wallet'
                />
              )}
              {staffPayments && (
                <SidebarMenuItem
                  to='/apps/payment-gateways-report'
                  title='گزارش درگاه‌های پرداخت'
                  fontIcon='la la-chart-line'
                />
              )}
              {staffServices && (
                <SidebarMenuItem
                  to='/apps/service-pricing'
                  title='قیمت‌گذاری سرویس‌ها'
                  fontIcon='la la-tags'
                />
              )}
              {staffMonitoring && (
                <SidebarMenuItem
                  to='/monitoring/send-dashboard'
                  title='داشبورد ارسال و استعلام'
                  fontIcon='la la-tachometer-alt'
                />
              )}
              {staffMonitoring && (
                <SidebarMenuItem
                  to='/monitoring/sso-logins'
                  title='آمار ورود با کارپوشه'
                  fontIcon='la la-sign-in-alt'
                />
              )}
              {staffMonitoring && (
                <SidebarMenuItem
                  to='/monitoring/website-stats'
                  title='آمار وب‌سایت'
                  fontIcon='la la-globe'
                />
              )}
              {isMegaPay && staffAccountingSync && (
                <SidebarMenuItem
                  to='/apps/accounting-database-settings'
                  title='تنظیمات دریافت از پایگاه داده'
                  fontIcon='la la-database'
                  isDesktopOnly
                />
              )}
              {isMegaPay && staffCan(currentUser, 'users') && (
                <SidebarMenuItem to='/apps/users' title='کاربران' fontIcon='la la-user-friends' />
              )}
              {staffCan(currentUser, 'partners') && (
                <SidebarMenuItem
                  to='/apps/partners'
                  title='همکاران API'
                  fontIcon='la la-handshake'
                />
              )}
              {smsLogEnabled && (
                <SidebarMenuItem
                  // checkIsActive matches by substring, so the module root keeps the item
                  // highlighted on both tabs; the index route redirects it to the log tab.
                  to='/apps/sms-log-management'
                  title='گزارش ارسال پیامک'
                  fontIcon='la la-comment-dots'
                  isDesktopOnly
                />
              )}
              {staffMonitoring && (
                <SidebarMenuItem
                  to='/monitoring/send-report'
                  title='گزارش ارسال و استعلام'
                  fontIcon='la la-clipboard-check'
                  isDesktopOnly
                />
              )}
              {staffMonitoring && (
                <SidebarMenuItem
                  to='/monitoring/send-watchdog'
                  title='تنظیمات نگهبان ارسال'
                  fontIcon='la la-shield-alt'
                  isDesktopOnly
                />
              )}
              {staffAccountingSync && (
                <SidebarMenuItem
                  to='/apps/accounting-sync/monitor'
                  title='پایش اتصال‌های حسابداری'
                  fontIcon='la la-heartbeat'
                  isDesktopOnly
                />
              )}
              {isMegaPay && staffCan(currentUser, 'catalog') && (
                <SidebarMenuItem
                  to='/apps/stuffid-catalog-import'
                  title='به‌روزرسانی شناسه‌های کالا/خدمت'
                  fontIcon='la la-file-upload'
                />
              )}
              {/* The categories page is an editor: «مدیریت» only. */}
              {isMegaPay && staffCan(currentUser, 'catalog', 'manage') && (
                <SidebarMenuItem
                  to='/apps/StuffidFileTag-management'
                  title='دسته بندی شناسه کالا/خدمت'
                  fontIcon='las la-layer-group'
                />
              )}
            </SidebarMenuItemWithSub>
          )}
          {supplyChainStaff && (
            <SidebarMenuItemWithSub
              to=''
              title='زنجیره تأمین (آسا)'
              fontIcon='la la-project-diagram'
              activePaths={[SUPPLY_CHAIN_STAFF_ROOT]}
            >
              {SUPPLY_CHAIN_STAFF_PAGES.map((section) => (
                <SidebarMenuItem key={section.to} to={section.to} title={section.title} fontIcon={`las ${section.icon}`} />
              ))}
            </SidebarMenuItemWithSub>
          )}
          {(isOptional || isMegaPay || isTSP) && picksBusiness && (
            <SidebarMenuItem
              to='/apps/taxpayer-management/companies'
              title='کسب و کارها'
              fontIcon='la la-building'
            />
          )}
        </>
      )}
      {/* Goods, buyers and businesses work on a phone since 60.11.507, so the phone's menu lists them too. */}
      <>
        <SidebarMenuItem
          to='/apps/product-management/products'
          title='کالاها/خدمات'
          fontIcon='la la-dolly'
          isDisabled={noBusiness && !picksBusiness}
          businessFilter
        />
        <SidebarMenuItem
          isDisabled={noBusiness && !picksBusiness}
          to='/apps/customer-management/customers'
          title='خریداران'
          fontIcon='la la-users'
          businessFilter
        />
      </>
      {(isOptional || isMegaPay || isTSP) && currentUser && currentUser.rl == 0 && !isStaff(currentUser) && (
        <SidebarMenuItem
          to='/apps/taxpayer-management/companies'
          title='کسب و کارها'
          fontIcon='la la-building'
        />
        // <SidebarMenuItemWithSub to={''} title={'کسب و کارها'} fontIcon='la la-users'>
        //   <SidebarMenuItem
        //     to='/apps/taxpayer-management/users'
        //     title='کسب و کارهای حقیقی'
        //     fontIcon='la la-user-tie'
        //   />
        // </SidebarMenuItemWithSub>
      )}
      {/* The bank's own install looks IDs up in its forms but has no ID page (the user, 2026-10-25). */}
      {isMegaPay && !isSupportSession && !isOnPremises && (
        <SidebarMenuItem
          type='href'
          to={getStuffIdSearchUrl()}
          title='شناسه‌های عمومی و اختصاصی'
          fontIcon='la la-search'
        />
      )}
      {isPOS && !isOnPremises && (
        <>
          {!onTSP && (
            <>
              <div
                onClick={() => {
                  PosSetting()
                }}
              >
                <SidebarMenuItem
                  type='href'
                  to={`#`}
                  title='تنظیمات کارت خوان'
                  fontIcon='la la-cogs'
                  target='_self'
                />
              </div>
            </>
          )}
          <div
            onClick={() => {
              const prcnt = PosBattery()
              alert(`باتری دستگاه: ${prcnt}` + (prcnt < 60 ? 'دستگاه را به شارژ متصل کنید.' : ''))
            }}
          >
            <SidebarMenuItem
              type='href'
              to={`#`}
              title='باتری کارت خوان'
              fontIcon='la la-battery-half'
              target='_self'
            />
          </div>
        </>
      )}
      {(isAPP || window.location.href.includes('pos')) && (
        <>
          <div
            onClick={() => {
              AppClearCache()
            }}
          >
            <SidebarMenuItem
              type='href'
              to={`#`}
              title='بروزرسانی'
              fontIcon='la la-sync-alt'
              target='_self'
            />
          </div>
        </>
      )}
      {(isOptional || isMegaPay) && !isSupportSession && ticketingEnabled && (
        <>
          <SidebarMenuItem to='/apps/ticket-management' title='تیکت' fontIcon='la la-life-ring' />
        </>
      )}
      {/* The former «امکانات بیشتر» group (user, 1405/07/30): bulk cancellation is already near the
          top, the rest closes the menu. */}
      {!isSupportSession && !isOnPremises && (
        <SidebarMenuItem
          to='/apps/product-management/online-prices'
          title='قیمت‌های آنلاین'
          fontIcon='la la-chart-line'
        />
      )}
      {!isSupportSession && (
        <SidebarMenuItem
          to='/extra-service/companyInquery'
          title='استعلام شناسه ملی'
          fontIcon='la la-info'
        />
      )}
    </>
  )
}

export {SidebarMenuMain}
